import { useQuery } from "@tanstack/react-query";
import { useContext } from "react";
import { Country } from "@lookiero/sty-psp-locale";
import { Segment } from "@lookiero/sty-psp-segment";
import { Tradename } from "@lookiero/sty-sp-tradename";
import { AnchorProduct } from "../../../domain/projections/AnchorProduct";
import { AnchorProductRepositoryImpl } from "../../domain/anchorProductRepositoryImpl/AnchorProductRepositoryImpl";
import { queryClientContext } from "../hooks/useQueryClient";
import { useTrackAnchorItemList } from "../tracking/useTrackAnchorItemList";

const anchorProductRepo = AnchorProductRepositoryImpl.getInstance();

interface UseGetAnchorProductsFunctionArgs {
  readonly auth: string;
  readonly userId: string;
  readonly country: Country;
  readonly segment: Segment;
  readonly tradename: Tradename;
  readonly apiUrl: () => string;
}

interface UseGetAnchorProductsFunction {
  (args: UseGetAnchorProductsFunctionArgs): AnchorProduct[] | undefined;
}

const useGetAnchorProducts: UseGetAnchorProductsFunction = ({ auth, country, segment, tradename, userId, apiUrl }) => {
  const trackAnchorItemList = useTrackAnchorItemList({
    segment,
    tradename,
    country,
    userId,
  });

  const queryClient = useContext(queryClientContext);

  const { data } = useQuery(
    {
      queryKey: ["useGetAnchorProducts", auth],
      queryFn: async () => {
        const anchorProducts = await anchorProductRepo.get(auth, apiUrl);

        trackAnchorItemList({
          items: anchorProducts.map(({ id, order }) => ({ item_id: id, item_order: order })),
        });

        return anchorProducts;
      },
      enabled: Boolean(auth),
    },
    queryClient,
  );

  return data;
};

export { useGetAnchorProducts };
